
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, Button, FormSelect } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { useNotification } from '../context/NotificationContext.jsx';
import * as Tabs from '@radix-ui/react-tabs';

// --- THEME CONFIGURATION ---
const THEMES = {
    dark: {
        bg: 'bg-[#1e1e1e]',
        sidebar: 'bg-[#252526]',
        activityBar: 'bg-[#333333]',
        panel: 'bg-[#1e1e1e]',
        header: 'bg-[#3c3c3c]',
        tab: 'bg-[#2d2d2d]',
        activeTab: 'bg-[#1e1e1e]',
        text: 'text-[#cccccc]',
        textDim: 'text-gray-400',
        border: 'border-[#333333]',
        accent: 'bg-[#007acc]',
        hover: 'hover:bg-[#2a2d2e]',
        editorTheme: 'vs-dark'
    },
    light: {
        bg: 'bg-[#ffffff]',
        sidebar: 'bg-[#f3f3f3]',
        activityBar: 'bg-[#e8e8e8]',
        panel: 'bg-[#ffffff]',
        header: 'bg-[#f3f3f3]',
        tab: 'bg-[#ececec]',
        activeTab: 'bg-[#ffffff]',
        text: 'text-[#333333]',
        textDim: 'text-gray-500',
        border: 'border-[#e0e0e0]',
        accent: 'bg-[#007acc]',
        hover: 'hover:bg-[#e8e8e8]',
        editorTheme: 'light'
    }
};

// --- HELPER COMPONENT: FILE TREE NODE ---
const FileNode = ({ node, onSelect, activePath, depth = 0, themeStyles }) => {
    const [isOpen, setIsOpen] = useState(true);
    const isFolder = node.type === 'folder';

    const handleSelect = (e) => {
        e.stopPropagation();
        if (isFolder) setIsOpen(!isOpen);
        else onSelect(node);
    };

    return (
        <div>
            <div
                onClick={handleSelect}
                className={`flex items-center px-2 py-1 cursor-pointer text-sm select-none ${activePath === node.path ? `${themeStyles.accent} text-white` : `${themeStyles.text} ${themeStyles.hover}`}`}
                style={{ paddingLeft: `${depth * 12 + 8}px` }}
            >
                <span className="mr-1 opacity-80 decoration-0">
                    {isFolder ? (
                        <DynamicIcon name={isOpen ? 'ChevronDown' : 'ChevronRight'} className="w-4 h-4" />
                    ) : (
                        <DynamicIcon name={node.path === 'connections.py' ? 'Lock' : 'FileText'} className={`w-4 h-4 ${activePath === node.path ? 'text-white' : 'text-blue-400'}`} />
                    )}
                </span>
                <span className="truncate flex-1">{node.name}</span>
            </div>
            {isFolder && isOpen && node.children?.map(child => (
                <FileNode
                    key={child.path} node={child}
                    onSelect={onSelect} activePath={activePath}
                    depth={depth + 1} themeStyles={themeStyles}
                />
            ))}
        </div>
    );
};

// --- TERMINAL / OUTPUT PANEL ---
const OutputPanel = ({ isRunning, logs, structuredOutput, onClose, themeStyles }) => {
    const [activeTab, setActiveTab] = useState('terminal'); // 'terminal' | 'rich-output'

    // Simple Renderers
    const renderRichOutput = () => {
        if (!structuredOutput) return <div className={`italic p-4 ${themeStyles.textDim}`}>No rich output available.</div>;

        // 1. HTML
        if (structuredOutput._html) {
            return <div className="p-4 bg-white text-black h-full overflow-auto" dangerouslySetInnerHTML={{ __html: structuredOutput._html }} />;
        }
        // 2. Image
        if (structuredOutput._image) {
            return (
                <div className="p-4 h-full flex items-center justify-center bg-gray-900">
                    <img src={`data:image/png;base64,${structuredOutput._image}`} alt="Output" className="max-h-full max-w-full" />
                </div>
            );
        }
        // 3. JSON / Object
        return (
            <div className={`p-4 h-full overflow-auto text-sm font-mono ${themeStyles.bg === 'bg-[#ffffff]' ? 'bg-gray-50 text-green-700' : 'bg-[#1e1e1e] text-green-400'}`}>
                <pre>{JSON.stringify(structuredOutput, null, 2)}</pre>
            </div>
        );
    };

    return (
        <div className={`h-full flex flex-col ${themeStyles.panel} border-t ${themeStyles.border}`}>
            {/* Panel Tabs */}
            <div className={`flex border-b ${themeStyles.border} px-2 ${themeStyles.header}`}>
                <button
                    onClick={() => setActiveTab('terminal')}
                    className={`px-4 py-1.5 text-xs uppercase tracking-wide border-b-2 ${activeTab === 'terminal' ? `border-blue-500 ${themeStyles.text}` : `border-transparent ${themeStyles.textDim}`}`}
                >
                    Terminal
                </button>
                <button
                    onClick={() => setActiveTab('rich-output')}
                    className={`px-4 py-1.5 text-xs uppercase tracking-wide border-b-2 ${activeTab === 'rich-output' ? 'border-purple-500 ' + themeStyles.text : 'border-transparent ' + themeStyles.textDim}`}
                >
                    Rich Output <span className="text-[10px] ml-1 opacity-60">HTML/Chart</span>
                </button>
                <div className="flex-1" />
                <button onClick={onClose} className={`p-1 hover:bg-opacity-10 hover:bg-gray-500 ${themeStyles.textDim}`}><DynamicIcon name="X" className="w-4 h-4" /></button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden relative">
                {activeTab === 'terminal' && (
                    <div className={`h-full p-2 font-mono text-sm overflow-auto ${themeStyles.text}`}>
                        {isRunning && <div className="mb-2 text-yellow-500 font-bold animate-pulse">Running code...</div>}
                        <pre className="whitespace-pre-wrap">{typeof logs === 'object' ? JSON.stringify(logs, null, 2) : logs}</pre>
                        {!logs && !isRunning && <span className="opacity-50 text-xs">No output. Run the code to see logs.</span>}
                    </div>
                )}
                {activeTab === 'rich-output' && renderRichOutput()}
            </div>
        </div>
    );
};


// --- MAIN PAGE COMPONENT ---
const MLModelStudioPage = ({ token, contentId: propContentId, onBack, theme }) => {
    const params = useParams();
    const contentId = propContentId || params.contentId;
    const { addNotification } = useNotification();

    // Theme derivation
    const currentThemeMode = theme?.mode === 'dark' ? 'dark' : 'light';
    const TS = THEMES[currentThemeMode];

    // Data State
    const [contentDetails, setContentDetails] = useState(null);
    const [fileTree, setFileTree] = useState([]);
    const [dashboards, setDashboards] = useState([]); // Added for Insight Dashboard picker

    // Editor State
    const [originalFiles, setOriginalFiles] = useState({}); // { path: content }
    const [openFiles, setOpenFiles] = useState([]); // Array of paths
    const [activeFile, setActiveFile] = useState(null); // Path
    const [unsavedChanges, setUnsavedChanges] = useState({}); // { path: content }

    // Execution State
    const [showPanel, setShowPanel] = useState(true);
    const [panelLogs, setPanelLogs] = useState("");
    const [richOutput, setRichOutput] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const [currentRunId, setCurrentRunId] = useState(null);

    // UI State
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [activeSidebarView, setActiveSidebarView] = useState('explorer'); // 'explorer' | 'settings'
    const [isLoading, setIsLoading] = useState(true);
    const [fullScreen, setFullScreen] = useState(false);

    // Env Vars State
    const [envVars, setEnvVars] = useState([]); // [{key:'', value:''}]
    const [hasEnvChanges, setHasEnvChanges] = useState(false);

    // Resize State
    const [panelHeight, setPanelHeight] = useState(200);
    const isResizing = useRef(false);


    // Initial Load
    const fetchContent = useCallback(async () => {
        if (!contentId || contentId === 'undefined') {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const data = await apiFetch(`/contents/${contentId}`, token);
            setContentDetails(data);

            // Initialize Env Vars
            if (data.env_vars) {
                setEnvVars(Object.entries(data.env_vars).map(([k, v]) => ({ key: k, value: v })));
            } else {
                setEnvVars([]);
            }

            // Fetch available dashboards for picker
            try {
                const dashData = await apiFetch(`/dashboards`, token);
                setDashboards(Array.isArray(dashData) ? dashData : (dashData.items || []));
            } catch (e) {
                console.warn("Failed to fetch dashboards for picker", e);
            }

            // Add connections.py
            const connFile = {
                name: 'connections.py', path: 'connections.py', type: 'file',
                content: generateConnectionsFileContent(data.connections)
            };
            const tree = [connFile, ...data.file_tree];
            setFileTree(tree);

            // Populate file content cache
            const fileMap = {};
            const traverse = (nodes) => {
                nodes.forEach(n => {
                    if (n.type === 'file') fileMap[n.path] = n.content || '';
                    if (n.children) traverse(n.children);
                });
            };
            traverse(tree);
            setOriginalFiles(fileMap);

            // Default open file
            if (!activeFile) {
                const main = fileMap['main.py'] !== undefined ? 'main.py' : Object.keys(fileMap).find(k => k !== 'connections.py');
                if (main) {
                    setOpenFiles(prev => prev.includes(main) ? prev : [...prev, main]);
                    setActiveFile(main);
                }
            }

        } catch (e) {
            addNotification(e.message, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [contentId, token]);

    useEffect(() => { fetchContent(); }, [fetchContent]);

    // Env Var Handlers
    const handleEnvChange = (index, field, value) => {
        const newVars = [...envVars];
        newVars[index][field] = value;
        setEnvVars(newVars);
        setHasEnvChanges(true);
    };

    const addEnvVar = () => setEnvVars([...envVars, { key: '', value: '' }]);
    const removeEnvVar = (index) => {
        const newVars = [...envVars];
        newVars.splice(index, 1);
        setEnvVars(newVars);
        setHasEnvChanges(true);
    };

    const handleSaveEnvVars = async () => {
        const envObj = envVars.reduce((acc, curr) => {
            if (curr.key.trim()) acc[curr.key.trim()] = curr.value;
            return acc;
        }, {});

        try {
            await apiFetch(`/contents/${contentId}`, token, {
                method: 'PUT',
                body: JSON.stringify({ env_vars: envObj })
            });
            addNotification("Environment variables saved", "success");
            setHasEnvChanges(false);
            // Update local content details to reflect saved state if needed
            setContentDetails(prev => ({ ...prev, env_vars: envObj }));
        } catch (e) {
            addNotification("Failed to save variables", "error");
        }
    };


    // Helpers
    const generateConnectionsFileContent = (connections) => {
        let c = `# Read-only connections file\n# This file is auto-generated by AIDAS Apps\nimport json\n\n`;
        connections?.forEach(conn => {
            const safeName = conn.name.replace(/\W/g, '_').toLowerCase();
            let keys = {};

            // Try to parse schema to provide helpful keys
            if (conn.connection_schema) {
                try {
                    const schema = typeof conn.connection_schema === 'string' ? JSON.parse(conn.connection_schema) : conn.connection_schema;
                    // Schema is usually a list of fields or a dict. Assuming list of objects {key: 'host', ...} based on typical UI
                    if (Array.isArray(schema)) {
                        schema.forEach(field => keys[field.key || field.name] = "******");
                    } else {
                        // If it's a flat dict
                        Object.keys(schema).forEach(k => keys[k] = "******");
                    }
                } catch (e) {
                    keys = { "error": "Schema parse failed" };
                }
            } else {
                keys = { "note": "No schema defined" };
            }

            // Add provider if available
            if (conn.provider) keys['provider'] = conn.provider;

            c += `# Connection: ${conn.name}\n${safeName} = ${JSON.stringify(keys, null, 4)}\n\n`;
        });
        return c;
    };

    // --- File Operations ---
    const handleFileSelect = (node) => {
        if (!openFiles.includes(node.path)) {
            setOpenFiles([...openFiles, node.path]);
        }
        setActiveFile(node.path);
    };

    const handleCloseTab = (e, path) => {
        e.stopPropagation();
        const newOpen = openFiles.filter(p => p !== path);
        setOpenFiles(newOpen);
        if (activeFile === path) {
            setActiveFile(newOpen.length > 0 ? newOpen[newOpen.length - 1] : null);
        }
    };

    const handleEditorChange = (value) => {
        if (activeFile === 'connections.py') return; // Read only
        setUnsavedChanges(prev => ({ ...prev, [activeFile]: value }));
    };

    const handleSave = async (path = activeFile) => {
        if (!path) return;
        const finalContent = unsavedChanges[path] ?? originalFiles[path];

        try {
            await apiFetch(`/contents/${contentId}/file`, token, {
                method: 'PUT',
                body: JSON.stringify({ path: path, content: finalContent })
            });
            // Update original
            setOriginalFiles(prev => ({ ...prev, [path]: finalContent }));
            // Clear unsaved
            setUnsavedChanges(prev => {
                const n = { ...prev };
                delete n[path];
                return n;
            });
            addNotification(`Saved ${path}`, 'success');
        } catch (e) {
            addNotification(`Error saving ${path}: ` + e.message, 'error');
        }
    };

    const handleSaveAll = async () => {
        const pathsToSave = Object.keys(unsavedChanges);
        if (pathsToSave.length === 0) return;

        for (const path of pathsToSave) {
            await handleSave(path);
        }
        addNotification("All files saved.", "success");
    };

    const handleCreateItem = async (type) => {
        const name = prompt(`Enter ${type} name:`);
        if (!name) return;
        // Note: For simplicity, creating in root for now. Ideally should create in active folder.
        // Since we don't track active folder selection easily in this UI yet, we can default to root.
        const path = name;
        try {
            if (type === 'file') {
                await apiFetch(`/contents/${contentId}/file`, token, { method: 'PUT', body: JSON.stringify({ path, content: '' }) });
            } else {
                await apiFetch(`/contents/${contentId}/folder`, token, { method: 'POST', body: JSON.stringify({ path, content: '' }) });
            }
            fetchContent();
        } catch (e) { addNotification(e.message, 'error'); }
    };

    const handleUpload = async (e) => {
        if (!e.target.files.length) return;
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append("file", file);
        try {
            await apiFetch(`/contents/${contentId}/upload`, token, { method: "POST", body: formData }, true);
            fetchContent();
            addNotification("File uploaded", "success");
        } catch (e) { addNotification(e.message, "error"); }
    };

    const handlePublish = async () => {
        const version = prompt("Enter version to publish (e.g., 1.0.0):", "1.0.0");
        if (!version) return;
        try {
            await apiFetch(`/contents/${contentId}/publish?version=${encodeURIComponent(version)}`, token, { method: 'POST' });
            addNotification(`Content published as version ${version}`, 'success');
        } catch (e) {
            addNotification(e.message, 'error');
        }
    };

    const handleDatasetChange = async (e) => {
        const newVal = e.target.value;
        setSelectedDatasetId(newVal);
        try {
            await apiFetch(`/contents/${contentId}`, token, {
                method: 'PUT',
                body: JSON.stringify({ dataset_ids: newVal ? [newVal] : [] })
            });
            addNotification("Dataset linked successfully", "success");
        } catch (e) {
            addNotification("Failed to update dataset link", "error");
        }
    };

    const handleDashboardChange = async (e) => {
        const newVal = e.target.value || null;
        try {
            await apiFetch(`/contents/${contentId}`, token, {
                method: 'PUT',
                body: JSON.stringify({ dashboard_id: newVal })
            });
            setContentDetails(prev => ({ ...prev, dashboard_id: newVal }));
            addNotification("Insights Dashboard updated", "success");
        } catch (e) {
            addNotification("Failed to update dashboard setting", "error");
        }
    };


    const handleRun = async () => {
        await handleSaveAll(); // Auto save all before run
        setIsRunning(true);
        setShowPanel(true);
        setPanelLogs("Initializing container...");
        setRichOutput(null);

        try {
            const run = await apiFetch(`/contents/${contentId}/execute`, token, { method: 'POST' });
            setCurrentRunId(run.id);
            pollRun(run.id);
        } catch (e) {
            setPanelLogs(`Error: ${e.message}`);
            setIsRunning(false);
        }
    };

    const pollRun = async (runId) => {
        try {
            const res = await apiFetch(`/contents/${contentId}/runs/${runId}`, token);
            setPanelLogs(res.logs || "No logs.");
            if (res.results) {
                try { setRichOutput(JSON.parse(res.results)); } catch { setRichOutput({ raw: res.results }); }
            }
            if (res.status === 'Completed' || res.status === 'Failed') {
                setIsRunning(false);
            } else {
                setTimeout(() => pollRun(runId), 2000);
            }
        } catch (e) {
            setPanelLogs(`Polling Error: ${e.message}`);
            setIsRunning(false);
        }
    };

    // --- Resizing Logic ---
    const startResizing = useCallback(() => { isResizing.current = true; }, []);
    const stopResizing = useCallback(() => { isResizing.current = false; }, []);
    const resize = useCallback((e) => {
        if (isResizing.current) {
            // Calculate height from bottom
            const newHeight = window.innerHeight - e.clientY;
            if (newHeight > 50 && newHeight < window.innerHeight - 100) {
                setPanelHeight(newHeight);
            }
        }
    }, []);

    useEffect(() => {
        window.addEventListener('mousemove', resize);
        window.addEventListener('mouseup', stopResizing);
        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [resize, stopResizing]);


    // Derived
    const currentEditorValue = activeFile ? (unsavedChanges[activeFile] ?? originalFiles[activeFile] ?? '') : '';
    const isDirty = activeFile && unsavedChanges[activeFile] !== undefined;
    const isAppReadOnly = contentDetails?.readonly === true;
    const isEditorReadOnly = isAppReadOnly || activeFile === 'connections.py';
    const hasAnyUnsaved = Object.keys(unsavedChanges).length > 0;

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className={`flex flex-col w-full overflow-hidden font-sans ${TS.bg} ${TS.text} ${fullScreen ? 'fixed inset-0 z-50' : 'h-full'}`}>

            {/* TOP BAR / TITLE BAR */}
            <header className={`flex items-center justify-between px-4 py-2 ${TS.header} border-b ${TS.border} h-12 flex-shrink-0 select-none`}>
                <div className="flex items-center gap-4">
                    <Button onClick={onBack} variant="secondary" className="!p-1 !h-8 !border-[#555] !text-gray-500">
                        <DynamicIcon name="ChevronLeft" className="w-4 h-4" /> Back
                    </Button>
                    <span className="font-semibold text-sm opacity-90 flex items-center gap-2">
                        {contentDetails?.name}
                        {isAppReadOnly && <span className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase border border-amber-200">Read Only</span>}
                        <span className="opacity-50 text-xs font-normal">ML Studio</span>
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={handleRun} disabled={isRunning} title="Run Code" className="bg-green-600 hover:bg-green-700 text-white p-1.5 rounded disabled:opacity-50 transition-colors">
                        <DynamicIcon name="Play" className="w-4 h-4" />
                    </button>
                    <button
                        onClick={async () => {
                            if (!currentRunId) return;
                            try {
                                await apiFetch(`/contents/${contentId}/runs/${currentRunId}/stop`, token, { method: 'POST' });
                                addNotification("Stop signal sent...", "info");
                            } catch (e) {
                                addNotification("Failed to stop: " + e.message, "error");
                            }
                        }}
                        disabled={!isRunning}
                        title="Stop Execution"
                        className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded disabled:opacity-50 disabled:bg-gray-500 transition-colors ml-1"
                    >
                        <DynamicIcon name="Square" className="w-4 h-4" />
                    </button>



                    <div className="h-6 w-px bg-gray-600 mx-2" />

                    <button onClick={() => handleSave(activeFile)} disabled={!isDirty || isAppReadOnly} title="Save (Ctrl+S)" className={`p-1.5 rounded transition-colors flex items-center gap-1 ${(isDirty && !isAppReadOnly) ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 opacity-50'}`}>
                        <DynamicIcon name="Save" className="w-4 h-4" />
                        {activeFile && <span className="text-xs max-w-[100px] truncate">{activeFile.split('/').pop()}</span>}
                    </button>
                    <button onClick={handleSaveAll} disabled={!hasAnyUnsaved || isAppReadOnly} title="Save All" className={`p-1.5 rounded transition-colors ${(hasAnyUnsaved && !isAppReadOnly) ? 'text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30' : 'text-gray-400 opacity-50'}`}>
                        <DynamicIcon name="SaveAll" className="w-4 h-4" />
                    </button>

                    <div className="h-6 w-px bg-gray-600 mx-2" />

                    <button onClick={() => setShowPanel(!showPanel)} title="Toggle Terminal" className={`p-1.5 rounded ${showPanel ? 'text-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
                        <DynamicIcon name="Terminal" className="w-4 h-4" />
                    </button>

                    <button onClick={() => setFullScreen(!fullScreen)} title="Toggle Fullscreen" className={`p-1.5 rounded text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700`}>
                        <DynamicIcon name={fullScreen ? "Minimize" : "Maximize"} className="w-4 h-4" />
                    </button>
                </div>
            </header>

            {/* MAIN WORKSPACE */}
            <div className="flex flex-1 overflow-hidden relative">

                {/* ACTIVITY BAR */}
                <div className={`w-12 ${TS.activityBar} flex flex-col items-center py-4 gap-4 z-10 flex-shrink-0`}>
                    <div
                        className={`p-2 rounded cursor-pointer ${sidebarVisible && activeSidebarView === 'explorer' ? `${currentThemeMode === 'dark' ? 'text-white' : 'text-blue-600'} border-l-2 border-blue-500` : `${currentThemeMode === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}`}
                        onClick={() => { setSidebarVisible(true); setActiveSidebarView('explorer'); }}
                        title="Explorer"
                    >
                        <DynamicIcon name="Files" className="w-6 h-6" />
                    </div>
                    <div
                        className={`p-2 rounded cursor-pointer ${sidebarVisible && activeSidebarView === 'settings' ? `${currentThemeMode === 'dark' ? 'text-white' : 'text-blue-600'} border-l-2 border-blue-500` : `${currentThemeMode === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}`}
                        onClick={() => { setSidebarVisible(true); setActiveSidebarView('settings'); }}
                        title="Configuration"
                    >
                        <DynamicIcon name="Settings" className="w-6 h-6" />
                    </div>
                </div>

                {/* SIDEBAR */}
                {sidebarVisible && (
                    <div className={`w-64 ${TS.sidebar} border-r ${TS.border} flex flex-col flex-shrink-0 transition-colors`}>
                        {activeSidebarView === 'explorer' ? (
                            <>
                                <div className={`flex items-center justify-between p-3 ${TS.text} text-xs uppercase font-bold tracking-wider`}>
                                    <span>Explorer</span>
                                    <div className="flex gap-1">
                                        <button title="New File" disabled={isAppReadOnly} onClick={() => handleCreateItem('file')} className={`p-0.5 rounded ${isAppReadOnly ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-300 dark:hover:bg-gray-600'}`}><DynamicIcon name="FilePlus" className="w-4 h-4" /></button>
                                        <button title="New Folder" disabled={isAppReadOnly} onClick={() => handleCreateItem('folder')} className={`p-0.5 rounded ${isAppReadOnly ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-300 dark:hover:bg-gray-600'}`}><DynamicIcon name="FolderPlus" className="w-4 h-4" /></button>
                                        <label title="Upload File" className={`p-0.5 rounded ${isAppReadOnly ? 'opacity-30 cursor-not-allowed pointer-events-none' : 'hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer'}`}>
                                            <input type="file" className="hidden" disabled={isAppReadOnly} onChange={handleUpload} />
                                            <DynamicIcon name="Upload" className="w-4 h-4" />
                                        </label>
                                        <button title="Refresh" onClick={fetchContent} className="hover:bg-gray-300 dark:hover:bg-gray-600 p-0.5 rounded"><DynamicIcon name="RefreshCw" className="w-4 h-4" /></button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto">
                                    {fileTree.map(node => (
                                        <FileNode
                                            key={node.path} node={node}
                                            onSelect={handleFileSelect} activePath={activeFile}
                                            themeStyles={TS}
                                        />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="p-4">
                                <div className={`text-sm font-semibold mb-2 ${TS.text}`}>Configuration</div>

                                {/* Dashboard Picker */}
                                <div className="mb-6">
                                    <div className={`${TS.textDim} text-sm font-medium mb-1`}>Insights Dashboard</div>
                                    <div className="text-xs text-gray-400 mb-2">Select a dashboard to show on the Insights tab when deployed to users.</div>
                                    <FormSelect
                                        value={contentDetails?.dashboard_id || ""}
                                        onChange={handleDashboardChange}
                                        disabled={isAppReadOnly}
                                        className={`w-full text-xs py-1.5 ${TS.bg} ${TS.text} border ${TS.border} rounded`}
                                    >
                                        <option value="">-- No Dashboard --</option>
                                        {dashboards.map(d => (
                                            <option key={d.id} value={d.id}>{d.name}</option>
                                        ))}
                                    </FormSelect>
                                </div>

                                <div className={`text-sm font-semibold mt-4 mb-2 ${TS.textDim}`}>Environment Variables</div>
                                <div className={`${TS.textDim} text-xs mb-4`}>
                                    Environment variables are injected into the runtime during execution.<p>Use os.environ.get("KEY") to access them.</p>
                                </div>

                                <div className="space-y-2 mb-4">
                                    {envVars.map((v, idx) => (
                                        <div key={idx} className="flex gap-2 items-center">
                                            <input
                                                className={`flex-1 min-w-0 bg-transparent border ${TS.border} rounded px-2 py-1 text-xs ${TS.text}`}
                                                placeholder="KEY"
                                                value={v.key}
                                                onChange={(e) => handleEnvChange(idx, 'key', e.target.value)}
                                                disabled={isAppReadOnly}
                                            />
                                            <input
                                                className={`flex-1 min-w-0 bg-transparent border ${TS.border} rounded px-2 py-1 text-xs ${TS.text}`}
                                                placeholder="VALUE"
                                                value={v.value}
                                                onChange={(e) => handleEnvChange(idx, 'value', e.target.value)}
                                                disabled={isAppReadOnly}
                                            />
                                            <button
                                                onClick={() => removeEnvVar(idx)}
                                                className="text-red-500 hover:text-red-400"
                                                disabled={isAppReadOnly}
                                            >
                                                <DynamicIcon name="Trash2" className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {!isAppReadOnly && (
                                    <div className="flex justify-between">
                                        <Button size="small" variant="secondary" onClick={addEnvVar}>Add</Button>

                                        {hasEnvChanges && (
                                            <Button size="small" onClick={handleSaveEnvVars}>Save Settings</Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* EDITOR AREA GROUP */}
                <div className={`flex-1 flex flex-col ${TS.bg} min-w-0 transition-colors`}>

                    {/* TABS HEADER */}
                    <div className={`flex ${TS.header} overflow-x-auto scrollbar-hide h-9 items-end flex-shrink-0`}>
                        {openFiles.map(path => (
                            <div
                                key={path}
                                onClick={() => setActiveFile(path)}
                                className={`
                                    group flex items-center gap-2 px-3 py-1.5 min-w-[120px] max-w-[200px] text-xs border-r ${TS.border} cursor-pointer h-full select-none
                                    ${activeFile === path ? `${TS.activeTab} ${TS.text} border-t-2 border-t-blue-500` : `${TS.tab} ${TS.textDim} hover:${TS.bg}`}
                                `}
                            >
                                <DynamicIcon name={path.endsWith('.py') ? 'FileCode' : 'FileText'} className="w-3 h-3 opacity-70" />
                                <span className={`truncate flex-1 ${unsavedChanges[path] ? 'italic font-semibold' : ''}`}>{path.split('/').pop()}</span>
                                <span
                                    onClick={(e) => handleCloseTab(e, path)}
                                    className={`opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-500/20 ${unsavedChanges[path] ? 'bg-blue-500 rounded-full w-2 h-2 !p-0 !text-transparent overflow-hidden' : ''}`}
                                >
                                    {unsavedChanges[path] ? '.' : <DynamicIcon name="X" className="w-3 h-3" />}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* MONACO EDITOR */}
                    <div className="flex-1 relative overflow-hidden">
                        {activeFile ? (
                            <Editor
                                height="100%"
                                path={activeFile}
                                defaultLanguage="python"
                                language={activeFile.endsWith('.py') ? 'python' : 'markdown'}
                                theme={TS.editorTheme}
                                value={currentEditorValue}
                                onChange={handleEditorChange}
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 14,
                                    fontFamily: 'Consolas, "Courier New", monospace',
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    readOnly: isEditorReadOnly,
                                }}
                            />
                        ) : (
                            <div className={`flex flex-col items-center justify-center h-full ${TS.text} opacity-20 pointer-events-none select-none`}>
                                <DynamicIcon name="Command" className="w-32 h-32" />
                                <p className="mt-4 font-semibold">No File Open</p>
                            </div>
                        )}
                    </div>

                    {/* RESIZE HANDLE */}
                    {showPanel && (
                        <div
                            onMouseDown={startResizing}
                            className={`h-1 cursor-ns-resize hover:bg-blue-500 transition-colors z-20 ${TS.border} border-t bg-transparent`}
                        />
                    )}

                    {/* BOTTOM PANEL */}
                    {showPanel && (
                        <div style={{ height: panelHeight }} className="flex-shrink-0 relative">
                            <OutputPanel
                                isRunning={isRunning}
                                logs={panelLogs}
                                structuredOutput={richOutput}
                                onClose={() => setShowPanel(false)}
                                themeStyles={TS}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* STATUS BAR */}
            <div className={`bg-[#007acc] text-white text-xs h-6 flex items-center px-3 gap-4 select-none flex-shrink-0`}>
                <div className="flex items-center gap-1"><DynamicIcon name="GitBranch" className="w-3 h-3" /> <span>main</span></div>
                <div className="flex-1" />
                <span>Ln 1, Col 1</span>
                <span>UTF-8</span>
                <span>Python</span>
            </div>
        </div>
    );
};

export default MLModelStudioPage;